import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  FileText, 
  Calendar, 
  DollarSign,
  User,
  Building,
  CheckCircle,
  Clock,
  AlertCircle,
  Upload,
  Download,
  BarChart3,
  MapPin,
  Bug
} from 'lucide-react';
import ApiService from '../services/ApiService';
import WorkOrderImport from './WorkOrderImport';
import HistoricalWorkOrderImport from './HistoricalWorkOrderImport';
import GenerateInvoiceModal from './GenerateInvoiceModal';
import EnhancedWorkOrderModal from './EnhancedWorkOrderModal';

const WorkOrderManagement = ({ openWorkOrderId, onWorkOrderOpened }) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    // Load saved filter from localStorage, default to empty string
    return localStorage.getItem('workOrderStatusFilter') || '';
  });
  const [viewMode, setViewMode] = useState(() => {
    // Load saved view mode from localStorage, default to 'active'
    return localStorage.getItem('workOrderViewMode') || 'active';
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showHistoricalImportModal, setShowHistoricalImportModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [summary, setSummary] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    // Load saved page size from localStorage, default to 20
    return parseInt(localStorage.getItem('workOrderPageSize') || '20');
  });


  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Normal', label: 'Normal' },
    { value: 'High', label: 'High' },
    { value: 'Urgent', label: 'Urgent' }
  ];

  useEffect(() => {
    loadWorkOrders();
    loadSummary();
  }, [currentPage, statusFilter, searchTerm, viewMode, pageSize]);

  // Reset to page 1 when search term, status filter, or view mode changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, viewMode]);

  // Save page size to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('workOrderPageSize', pageSize.toString());
  }, [pageSize]);

  // Open a specific work order when navigated from another tab
  useEffect(() => {
    if (!openWorkOrderId) return;
    ApiService.apiCall(`/workorder/${openWorkOrderId}`)
      .then(wo => {
        setSelectedWorkOrder(wo);
        setShowViewModal(true);
        if (onWorkOrderOpened) onWorkOrderOpened();
      })
      .catch(err => console.error('Error loading work order:', err));
  }, [openWorkOrderId]);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const loadWorkOrders = async () => {
    setLoading(true);
    try {
      // Determine the actual status filter based on view mode
      let actualStatusFilter = statusFilter;
      if (viewMode === 'finalized' && !statusFilter) {
        actualStatusFilter = 'Finalized';
      } else if (viewMode === 'active' && !statusFilter) {
        // Use special "Active" status to filter out finalized on backend
        actualStatusFilter = 'Active';
      }

      const params = {
        page: currentPage,
        pageSize: pageSize,
        status: actualStatusFilter,
        clientName: searchTerm,
        subcontractorName: searchTerm, // Also search subcontractors with the same term
        sortBy: 'DateCreated',
        sortOrder: 'desc'
      };

      console.log('📋 Loading work orders with params:', params);
      const response = await ApiService.getWorkOrders(params);
      console.log('📊 Work orders response:', response);
      
      // Handle the new response structure with pagination
      const workOrdersData = response.data || response; // Fallback for backward compatibility
      const paginationInfo = response.pagination;
      
      console.log('📊 Number of work orders:', workOrdersData.length);
      console.log('📄 Pagination info:', paginationInfo);
      
      // No frontend filtering needed - backend now handles "Active" status filter
      setWorkOrders(workOrdersData);
      
      // Use pagination info from API response
      if (paginationInfo) {
        setTotalPages(paginationInfo.totalPages);
        setTotalCount(paginationInfo.totalCount);
        console.log('📄 Pagination: total =', paginationInfo.totalCount, 'pages =', paginationInfo.totalPages);
      } else {
        // Fallback for backward compatibility
        const totalCountFallback = workOrdersData.length;
        setTotalPages(Math.ceil(totalCountFallback / pageSize));
        setTotalCount(totalCountFallback);
        console.log('📄 Fallback pagination: total =', totalCountFallback, 'pages =', Math.ceil(totalCountFallback / pageSize));
      }
    } catch (error) {
      console.error('Error loading work orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (viewMode) params.append('viewMode', viewMode);
      const summaryData = await ApiService.apiCall(`/workorder/summary?${params.toString()}`);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const handleCreateWorkOrder = async (formData) => {
    try {
      console.log('📝 Creating work order with data:', formData);
      const { followUpTasks, ...workOrderData } = formData;
      const result = await ApiService.apiCall('/workorder', { method: 'POST', body: JSON.stringify(workOrderData) });
      console.log('✅ Work order created successfully:', result);

      if (followUpTasks?.length > 0) {
        await Promise.allSettled(followUpTasks.map(task =>
          ApiService.apiCall('/workordertask', {
            method: 'POST',
            body: JSON.stringify({ workOrderId: result.id, title: task.title, description: task.description || null, dueDate: task.dueDate || null, assignedTo: task.assignedTo || null })
          })
        ));
      }

      setShowCreateModal(false);
      await loadWorkOrders();
      await loadSummary();
    } catch (error) {
      console.error('Error creating work order:', error);
      alert('Error creating work order: ' + error.message);
    }
  };

  const handleUpdateWorkOrder = async (id, formData) => {
    try {
      console.log('📝 Updating work order ID:', id);
      console.log('📝 Update data:', formData);
      
      const result = await ApiService.updateWorkOrder(id, formData);
      console.log('✅ Work order updated successfully:', result);
      
      setShowEditModal(false);
      setSelectedWorkOrder(null);
      
      console.log('🔄 Reloading work orders after update...');
      await loadWorkOrders();
      await loadSummary();
      console.log('✅ Update reload complete');
    } catch (error) {
      console.error('💥 Error updating work order:', error);
      console.error('💥 Error details:', error.message);
      alert('Error updating work order: ' + error.message);
    }
  };

  const handleDeleteWorkOrder = async (id) => {
    if (window.confirm('Are you sure you want to delete this work order?')) {
      setLoading(true);
      try {
        console.log('🗑️ Deleting work order:', id);
        await ApiService.apiCall(`/workorder/${id}`, { method: 'DELETE' });
        console.log('✅ Work order deleted successfully');
        loadWorkOrders();
        loadSummary();
      } catch (error) {
        console.error('❌ Error deleting work order:', error);
        
        let errorMessage = 'Error deleting work order: ';
        if (error.status === 404) {
          errorMessage += 'Work order not found or may have already been deleted.';
        } else if (error.status === 401) {
          errorMessage += 'Authentication required to delete work orders.';
        } else if (error.status === 403) {
          errorMessage += 'You do not have permission to delete this work order.';
        } else if (error.status === 409) {
          errorMessage += 'Cannot delete work order - it may have dependent records (staged items, etc.).';
        } else {
          errorMessage += error.message;
        }
        
        alert(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleImportComplete = (results) => {
    console.log('Import completed:', results);
    loadWorkOrders();
    loadSummary();
  };

  const handleInvoiceGenerated = (result) => {
    console.log('Invoice generated:', result);
    alert(`Invoice generated successfully: ${result.invoiceNumber}`);
    loadWorkOrders();
    loadSummary();
  };

  const handleMarkComplete = async (workOrderId) => {
    if (!window.confirm('Mark this work order as Completed?')) {
      return;
    }

    setLoading(true);
    try {
      await ApiService.updateWorkOrder(workOrderId, {
        status: 'Completed'
      });
      
      alert('✅ Work order marked as Completed!');
      loadWorkOrders();
      loadSummary();
    } catch (error) {
      console.error('Error marking work order as complete:', error);
      alert('Failed to mark work order as complete: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDebugWorkOrder = async (workOrderId) => {
    console.log('🚨 DEBUG BUTTON CLICKED! Work Order ID:', workOrderId);
    setLoading(true);
    
    try {
      console.log('🔍 Starting manual debug process...');
      
      // Find the work order in our current data
      const workOrder = workOrders.find(wo => wo.id === workOrderId);
      console.log('📋 Found work order:', workOrder);
      
      if (!workOrder) {
        alert('Work order not found in current data');
        return;
      }
      
      // Try to finalize and see what error we get
      console.log('🧪 Attempting finalization to see what error occurs...');
      
      try {
        const result = await ApiService.finalizeWorkOrder(workOrderId, {
          invoiceDate: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
        
        // If it succeeds, show success with option to clear and retest
        const retestChoice = window.confirm(`✅ SUCCESS! Work order can be finalized!\n\nResult: ${result.message}\n\nStaged Items:\n• Invoice: ${result.invoiceNumber}\n• Bills: ${result.billNumbers?.join(', ') || 'None'}\n\n🔄 Click OK to CLEAR staging and reset for retesting\nClick Cancel to keep staged items`);
        
        if (retestChoice) {
          try {
            console.log('🗑️ Clearing staging for work order to allow retesting...');
            await ApiService.clearWorkOrderStaging(workOrderId);
            console.log('✅ Staging cleared successfully');
            alert('✅ Staging cleared! Work order reset to "Completed" status. You can now retest the finalization.');
          } catch (clearError) {
            console.error('❌ Error clearing staging:', clearError);
            alert('❌ Error clearing staging: ' + clearError.message);
          }
        }
        
        loadWorkOrders();
        loadSummary();
        
      } catch (finalizationError) {
        console.log('🔍 Finalization error (this tells us what\'s wrong):', finalizationError);
        
        let debugMessage = `🔍 Work Order Debug Information\n\n`;
        debugMessage += `Work Order: ${workOrder.workOrderNumber}\n`;
        debugMessage += `Status: ${workOrder.status}\n`;
        debugMessage += `Client: ${workOrder.clientName}\n`;
        debugMessage += `Line Items: ${workOrder.lineItems?.length || 0}\n\n`;
        
        // Analyze the error to provide specific feedback
        console.log('🔍 Full error object:', finalizationError);
        console.log('🔍 Error message:', finalizationError.message);
        console.log('🔍 Error response:', finalizationError.response);
        
        if (finalizationError.status === 400) {
          debugMessage += `❌ Cannot Finalize - Reason:\n`;
          debugMessage += `${finalizationError.message || 'Bad Request - check requirements'}\n\n`;
          
          if (finalizationError.message.includes('Completed')) {
            debugMessage += `🔧 Fix: Change work order status to "Completed"\n`;
          } else if (finalizationError.message.includes('approved')) {
            debugMessage += `🔧 Fix: Ensure client approval exists and status is "Approved"\n`;
            debugMessage += `\n📋 Check these:\n`;
            debugMessage += `• Work order number: ${workOrder.workOrderNumber}\n`;
            debugMessage += `• Client approval must exist with same work order number\n`;
            debugMessage += `• Client approval status must be "Approved"\n`;
            debugMessage += `• Re-import client approvals if status codes were numeric\n`;
          }
        } else if (finalizationError.status === 404) {
          debugMessage += `❌ Work order not found in database\n`;
        } else if (finalizationError.status === 401) {
          debugMessage += `❌ Authentication error - please log in\n`;
        } else {
          debugMessage += `❌ Error: ${finalizationError.message || finalizationError.toString() || 'Unknown error'}\n`;
          debugMessage += `Status Code: ${finalizationError.status || 'Unknown'}\n`;
          debugMessage += `Error Type: ${typeof finalizationError}\n`;
        }
        
        console.log('📝 Debug analysis:', debugMessage);
        alert(debugMessage);
      }
      
    } catch (error) {
      console.error('❌ Error in debug process:', error);
      alert(`Debug error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeWorkOrder = async (workOrderId) => {
    if (window.confirm('This will stage the invoice and bills for Xero creation. Continue?')) {
      setLoading(true);
      try {
        console.log('🔄 Starting finalization process for work order:', workOrderId);
        
        const result = await ApiService.finalizeWorkOrder(workOrderId, {
          invoiceDate: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
        
        console.log('✅ Finalization successful:', result);
        
        alert(`Work order finalized!\n\n${result.message}\n\nStaged Items:\n• Invoice: ${result.invoiceNumber}\n• Bills: ${result.billNumbers.join(', ')}`);
        loadWorkOrders();
        loadSummary();
      } catch (error) {
        console.error('❌ Error finalizing work order:', error);
        
        // Enhanced error handling with more specific messages
        let errorMessage = 'Error finalizing work order: ';
        
        if (error.status === 401 || error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage += 'Authentication required. Please log in to continue with staging operations.';
        } else if (error.status === 404 || error.message.includes('404')) {
          errorMessage += 'Work order not found or may have been deleted.';
        } else if (error.status === 400 || error.message.includes('400')) {
          errorMessage += 'Invalid request. The work order may not be in the correct status for finalization (must be "Completed" and client-approved).';
          errorMessage += '\n\n💡 Tip: Use the "Debug" button to see what\'s preventing finalization.';
        } else if (error.status === 500 || error.message.includes('500')) {
          errorMessage += 'Server error occurred during staging. Please try again or contact support.';
        } else if (error.isNetworkError || error.message.includes('Network error') || error.message.includes('fetch')) {
          errorMessage += 'Network connection issue. Please check your connection and ensure the API server is running.';
        } else {
          errorMessage += error.message;
        }
        
        // Show detailed error information in console for debugging
        console.error('🔍 Detailed error information:', {
          message: error.message,
          stack: error.stack,
          workOrderId: workOrderId,
          timestamp: new Date().toISOString()
        });
        
        alert(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'Assigned': { color: 'bg-blue-100 text-blue-800', icon: User },
      'InProgress': { color: 'bg-purple-100 text-purple-800', icon: AlertCircle },
      'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Finalized': { color: 'bg-emerald-100 text-emerald-800', icon: FileText },
      'Cancelled': { color: 'bg-red-100 text-red-800', icon: Trash2 }
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'Low': 'bg-gray-100 text-gray-800',
      'Normal': 'bg-blue-100 text-blue-800',
      'High': 'bg-orange-100 text-orange-800',
      'Urgent': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${priorityConfig[priority] || priorityConfig['Normal']}`}>
        {priority}
      </span>
    );
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    // Save to localStorage for persistence
    localStorage.setItem('workOrderStatusFilter', value);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    // Save to localStorage for persistence
    localStorage.setItem('workOrderViewMode', mode);
    // Reset status filter when changing view mode
    setStatusFilter('');
    localStorage.setItem('workOrderStatusFilter', '');
  };

  // Filter status options based on view mode
  const getStatusOptions = () => {
    if (viewMode === 'finalized') {
      return [
        { value: '', label: 'All Finalized' },
        { value: 'Finalized', label: 'Finalized' }
      ];
    } else {
      return [
        { value: '', label: 'All Active' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Assigned', label: 'Assigned' },
        { value: 'InProgress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' }
      ];
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Work Order Management
              {viewMode === 'finalized' && <span className="text-green-600"> - Archive</span>}
            </h1>
            <p className="text-gray-600 mt-1">
              {viewMode === 'active' 
                ? 'Manage your active work orders, track progress, and monitor costs'
                : 'View finalized work orders and historical data'
              }
            </p>
          </div>
          <div className="flex gap-2">
            {viewMode === 'active' && (
              <>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Import New
                </button>
                <button
                  onClick={() => setShowHistoricalImportModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Search className="w-4 h-4" />
                  Import Historical
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Work Order
                </button>
              </>
            )}
            {viewMode === 'finalized' && (
              <div className="text-sm text-gray-500 px-4 py-2">
                📁 Viewing archived work orders
              </div>
            )}
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-lg shadow border p-1 flex">
            <button
              onClick={() => handleViewModeChange('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'active'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              📋 Active Work Orders
            </button>
            <button
              onClick={() => handleViewModeChange('finalized')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'finalized'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              📁 Finalized (Archive)
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Work Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.totalWorkOrders}</p>
                  {(searchTerm || statusFilter) && <p className="text-xs text-blue-500">filtered</p>}
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">In Progress</p>
                  <p className="text-2xl font-bold text-purple-600">{summary.inProgressWorkOrders}</p>
                  <p className="text-xs text-gray-400">not completed/finalized</p>
                </div>
                <Clock className="w-8 h-8 text-purple-500" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{summary.completedWorkOrders}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">${summary.totalInvoiceAmount.toFixed(2)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Profit</p>
                  <p className="text-2xl font-bold text-emerald-600">${summary.totalProfit.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{summary.profitMargin.toFixed(1)}% margin</p>
                </div>
                <BarChart3 className="w-8 h-8 text-emerald-500" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by client name, subcontractor, or work order number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {getStatusOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Work Orders Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                      Actions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Work Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subcontractors
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {workOrders.map((workOrder) => (
                    <tr key={workOrder.id} className="hover:bg-gray-50">
                      {/* Actions Column - First and Sticky */}
                      <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium sticky left-0 bg-white z-10 shadow-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              console.log('👁️ Eye icon clicked for work order:', workOrder.workOrderNumber);
                              console.log('📋 Work order data:', workOrder);
                              setSelectedWorkOrder(workOrder);
                              setShowViewModal(true);
                              console.log('✅ Modal state set - showViewModal: true');
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedWorkOrder(workOrder);
                              setShowEditModal(true);
                            }}
                            disabled={workOrder.status === 'Finalized'}
                            className={`p-1 ${workOrder.status === 'Finalized' 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-yellow-600 hover:text-yellow-900'}`}
                            title={workOrder.status === 'Finalized' ? 'Cannot edit finalized work order' : 'Edit'}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {(workOrder.status === 'Pending' || workOrder.status === 'InProgress' || workOrder.status === 'Assigned') && (
                            <button
                              onClick={() => handleMarkComplete(workOrder.id)}
                              disabled={loading}
                              className="text-green-600 hover:text-green-900 p-1 disabled:opacity-50"
                              title="Mark as Completed"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {workOrder.status === 'Completed' && (
                            <button
                              onClick={() => handleDebugWorkOrder(workOrder.id)}
                              disabled={loading}
                              className="text-purple-600 hover:text-purple-900 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Finalize & Stage for Xero"
                            >
                              {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {workOrder.status === 'Completed' && (
                            <button
                              onClick={() => {
                                setSelectedWorkOrder(workOrder);
                                setShowInvoiceModal(true);
                              }}
                              className="text-green-600 hover:text-green-900 p-1"
                              title="Generate Invoice"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteWorkOrder(workOrder.id)}
                            disabled={workOrder.status === 'Finalized'}
                            className={`p-1 ${workOrder.status === 'Finalized' 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-red-600 hover:text-red-900'}`}
                            title={workOrder.status === 'Finalized' ? 'Cannot delete finalized work order' : 'Delete'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      {/* Work Order Number */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{workOrder.workOrderNumber}</div>
                        {workOrder.lineItems && workOrder.lineItems.length > 0 && workOrder.lineItems[0].notes && (
                          <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={workOrder.lineItems[0].notes}>
                            {workOrder.lineItems[0].notes}
                          </div>
                        )}
                      </td>
                      {/* Location */}
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">
                            {workOrder.location || <span className="text-gray-400">No location</span>}
                          </div>
                        </div>
                      </td>
                      {/* Subcontractors */}
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">
                            {workOrder.lineItems && workOrder.lineItems.length > 0 ? (
                              <div>
                                <div className="font-medium">{workOrder.lineItems[0].subcontractorName}</div>
                                {workOrder.lineItems.length > 1 && (
                                  <div className="text-xs text-gray-500">
                                    +{workOrder.lineItems.length - 1} more
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">No subcontractors</span>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Amount */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${workOrder.invoiceAmount.toFixed(2)}
                      </td>
                      {/* Total Cost */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(workOrder.totalCost || 0).toFixed(2)}
                      </td>
                      {/* Profit */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`font-medium ${workOrder.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${workOrder.profit.toFixed(2)}
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(workOrder.status)}
                      </td>
                      {/* Priority */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPriorityBadge(workOrder.priority)}
                      </td>
                      {/* Created */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(workOrder.dateCreated).toLocaleDateString()}
                      </td>
                      {/* Client */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">{workOrder.clientName}</div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-700">
                    Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                  </div>
                  {totalPages > 1 && (
                    <div className="text-sm text-gray-500">
                      Page {currentPage} of {totalPages}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">per page</span>
                  </div>
                </div>
                
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="First page"
                    >
                      ««
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Previous page"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 text-sm border rounded ${
                              currentPage === pageNum
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Next page"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Last page"
                    >
                      »»
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <EnhancedWorkOrderModal
          title="Create New Work Order"
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateWorkOrder}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedWorkOrder && (
        <EnhancedWorkOrderModal
          title="Edit Work Order"
          workOrder={selectedWorkOrder}
          onClose={() => {
            setShowEditModal(false);
            setSelectedWorkOrder(null);
          }}
          onSubmit={(formData) => handleUpdateWorkOrder(selectedWorkOrder.id, formData)}
        />
      )}

      {/* View Modal */}
      {showViewModal && selectedWorkOrder && (
        <>
          {console.log('🔍 Rendering WorkOrderViewModal for:', selectedWorkOrder.workOrderNumber)}
          <WorkOrderViewModal
            workOrder={selectedWorkOrder}
            onClose={() => {
              console.log('❌ Closing view modal');
              setShowViewModal(false);
              setSelectedWorkOrder(null);
            }}
            onEdit={() => {
              console.log('✏️ Opening edit modal from view modal');
              setShowViewModal(false);
              setShowEditModal(true);
            }}
          />
        </>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <WorkOrderImport
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}

      {/* Historical Import Modal */}
      {showHistoricalImportModal && (
        <HistoricalWorkOrderImport
          onClose={() => setShowHistoricalImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}

      {/* Generate Invoice Modal */}
      {showInvoiceModal && selectedWorkOrder && (
        <GenerateInvoiceModal
          workOrder={selectedWorkOrder}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedWorkOrder(null);
          }}
          onInvoiceGenerated={handleInvoiceGenerated}
        />
      )}
    </div>
  );
};

// Create/Edit Modal Component
const WorkOrderModal = ({ title, workOrder, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    workOrderNumber: workOrder?.workOrderNumber || '',
    clientName: workOrder?.clientName || '',
    subcontractorName: workOrder?.lineItems?.[0]?.subcontractorName || '',
    invoiceAmount: workOrder?.invoiceAmount || 0,
    cost: workOrder?.lineItems?.[0]?.cost || 0,
    status: workOrder?.status || 'Pending',
    location: workOrder?.location || '',
    description: workOrder?.description || '',
    serviceType: workOrder?.serviceType || '',
    priority: workOrder?.priority || 'Normal',
    dueDate: workOrder?.dueDate ? workOrder.dueDate.split('T')[0] : '',
    department: workOrder?.department || '',
    team: workOrder?.team || '',
    notes: workOrder?.notes || '',
    tags: workOrder?.tags || ''
  });

  console.log('🔧 WorkOrderModal initialized with:', { workOrder, formData });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Auto-assign team from subcontractor if team is not already set
    let teamToAssign = formData.team;
    if (!teamToAssign && formData.subcontractorName) {
      // Use the subcontractor's name as the team
      teamToAssign = formData.subcontractorName;
    }
    
    // Convert string values to appropriate types
    const submitData = {
      ...formData,
      team: teamToAssign, // Set the team from subcontractor
      invoiceAmount: parseFloat(formData.invoiceAmount) || 0,
      originalInvoiceAmount: parseFloat(formData.invoiceAmount) || 0,
      clientDiscountPercentage: 0,
      clientDiscountAmount: 0,
      dueDate: formData.dueDate || null,
      lineItems: [{
        subcontractorName: formData.subcontractorName || 'Default Contractor',
        subcontractorContactId: null,
        cost: parseFloat(formData.cost) || 0,
        description: formData.description || '',
        serviceType: formData.serviceType || '',
        notes: formData.notes || ''
      }]
    };

    console.log('📤 Form submission data:', submitData);
    console.log('👷 Line items being sent:', submitData.lineItems);
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <span className="text-2xl">&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Work Order Number *
                </label>
                <input
                  type="text"
                  name="workOrderNumber"
                  value={formData.workOrderNumber}
                  onChange={handleChange}
                  required
                  disabled={!!workOrder} // Disable editing for existing work orders
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Pending">Pending</option>
                  <option value="Assigned">Assigned</option>
                  <option value="InProgress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subcontractor *
                </label>
                <input
                  type="text"
                  name="subcontractorName"
                  value={formData.subcontractorName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="invoiceAmount"
                  value={formData.invoiceAmount}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost *
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="cost"
                  value={formData.cost}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profit
                </label>
                <input
                  type="text"
                  value={`$${(parseFloat(formData.invoiceAmount) - parseFloat(formData.cost)).toFixed(2)}`}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Low">Low</option>
                  <option value="Normal">Normal</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type
                </label>
                <input
                  type="text"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="e.g., GAM, MCS"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team
                </label>
                <input
                  type="text"
                  name="team"
                  value={formData.team}
                  onChange={handleChange}
                  placeholder="e.g., rich, garrett, jason"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Comma-separated tags"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {workOrder ? 'Update' : 'Create'} Work Order
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Enhanced View Modal Component with Audit Links
const WorkOrderViewModal = ({ workOrder, onClose, onEdit }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'Assigned': { color: 'bg-blue-100 text-blue-800', icon: User },
      'InProgress': { color: 'bg-purple-100 text-purple-800', icon: AlertCircle },
      'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Finalized': { color: 'bg-emerald-100 text-emerald-800', icon: FileText },
      'Cancelled': { color: 'bg-red-100 text-red-800', icon: Trash2 }
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {status}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Work Order Details</h2>
              <p className="text-gray-600">#{workOrder.workOrderNumber}</p>
            </div>
            <div className="flex gap-2">
              {workOrder.status !== 'Finalized' && (
                <button
                  onClick={onEdit}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  title="Edit Work Order"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}
              <button 
                onClick={onClose} 
                className="text-gray-500 hover:text-gray-700 p-2"
                title="Close"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Status and Key Info Banner */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusBadge(workOrder.status)}
                    <span className="text-lg font-semibold text-gray-900">{workOrder.workOrderNumber}</span>
                  </div>
                  <p className="text-gray-700">{workOrder.clientName}</p>
                  {workOrder.location && <p className="text-sm text-gray-600">📍 {workOrder.location}</p>}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(workOrder.invoiceAmount)}</p>
                  <p className="text-sm text-gray-500">Invoice Amount</p>
                </div>
              </div>
            </div>

            {/* Xero Integration Status */}
            {(workOrder.xeroInvoiceId || workOrder.xeroInvoiceUrl || workOrder.xeroInvoiceNumber) && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2">
                  ✅ Xero Invoice Confirmation
                </h3>
                <div className="space-y-2">
                  {workOrder.xeroInvoiceNumber && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Invoice Number:</label>
                      <span className="ml-2 text-gray-900 font-mono">{workOrder.xeroInvoiceNumber}</span>
                    </div>
                  )}
                  {workOrder.xeroInvoiceId && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Xero Invoice ID:</label>
                      <span className="ml-2 text-gray-900 font-mono text-sm">{workOrder.xeroInvoiceId}</span>
                    </div>
                  )}
                  {workOrder.xeroInvoiceUrl && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">View in Xero:</label>
                      <a 
                        href={workOrder.xeroInvoiceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:text-blue-800 underline"
                      >
                        Open Invoice in Xero →
                      </a>
                    </div>
                  )}
                  {workOrder.dateInvoiced && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Invoice Created:</label>
                      <span className="ml-2 text-gray-900">{new Date(workOrder.dateInvoiced).toLocaleString()}</span>
                    </div>
                  )}
                  {workOrder.status === 'Finalized' && workOrder.dateFinalized && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Finalized:</label>
                      <span className="ml-2 text-gray-900">{new Date(workOrder.dateFinalized).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Work Order Number</label>
                    <p className="text-gray-900 font-mono">{workOrder.workOrderNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Priority</label>
                    <p className="text-gray-900">{workOrder.priority}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created Date</label>
                    <p className="text-gray-900">{new Date(workOrder.dateCreated).toLocaleDateString()}</p>
                  </div>
                  {workOrder.dueDate && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Due Date</label>
                      <p className="text-gray-900">{new Date(workOrder.dueDate).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Invoice Amount</label>
                    <p className="text-gray-900 font-semibold">{formatCurrency(workOrder.invoiceAmount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Cost</label>
                    <p className="text-gray-900">{formatCurrency(workOrder.totalCost || workOrder.cost)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Profit</label>
                    <p className={`font-semibold ${(workOrder.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(workOrder.profit)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Profit Margin</label>
                    <p className={`${(workOrder.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(workOrder.invoiceAmount && workOrder.invoiceAmount > 0) ? (((workOrder.profit || 0) / workOrder.invoiceAmount) * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">Client Name</label>
                  <p className="text-gray-900">{workOrder.clientName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Subcontractor</label>
                  {workOrder.lineItems && workOrder.lineItems.length > 0 ? (
                    <div className="space-y-1">
                      {workOrder.lineItems.map((lineItem, index) => (
                        <p key={index} className="text-gray-900">{lineItem.subcontractorName}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No subcontractors assigned</p>
                  )}
                </div>
              </div>
            </div>

            {/* Line Items / Subcontractor Details */}
            {workOrder.lineItems && workOrder.lineItems.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Subcontractor Details</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    {workOrder.lineItems.map((lineItem, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">Subcontractor</label>
                            <p className="text-gray-900 font-medium">{lineItem.subcontractorName}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Cost</label>
                            <p className="text-gray-900 font-semibold">{formatCurrency(lineItem.cost)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Status</label>
                            <p className="text-gray-900">{lineItem.status || 'Pending'}</p>
                          </div>
                        </div>
                        {lineItem.description && (
                          <div className="mt-3">
                            <label className="text-sm font-medium text-gray-500">Description</label>
                            <p className="text-gray-900">{lineItem.description}</p>
                          </div>
                        )}
                        {lineItem.serviceType && (
                          <div className="mt-2">
                            <label className="text-sm font-medium text-gray-500">Service Type</label>
                            <p className="text-gray-900">{lineItem.serviceType}</p>
                          </div>
                        )}
                        {/* Bill Information */}
                        {(lineItem.xeroBillId || lineItem.xeroBillNumber || lineItem.xeroBillUrl) && (
                          <div className={`mt-3 rounded-lg p-3 border ${
                            lineItem.status === 'Paid' 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-red-50 border-red-200'
                          }`}>
                            <h4 className={`text-sm font-semibold mb-2 ${
                              lineItem.status === 'Paid' 
                                ? 'text-green-800' 
                                : 'text-red-800'
                            }`}>
                              {lineItem.status === 'Paid' ? '✅ Bill Paid' : '📄 Bill Information'}
                            </h4>
                            <div className="space-y-1">
                              {lineItem.xeroBillNumber && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Bill Number:</label>
                                  <span className="ml-2 text-sm text-gray-900 font-mono">{lineItem.xeroBillNumber}</span>
                                </div>
                              )}
                              {lineItem.xeroBillId && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Xero Bill ID:</label>
                                  <span className="ml-2 text-sm text-gray-900 font-mono">{lineItem.xeroBillId}</span>
                                </div>
                              )}
                              {lineItem.xeroBillUrl && (
                                <div>
                                  <a 
                                    href={lineItem.xeroBillUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                                  >
                                    View Bill in Xero →
                                  </a>
                                </div>
                              )}
                              {lineItem.datePaid && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Date Paid:</label>
                                  <span className="ml-2 text-sm text-gray-900">{new Date(lineItem.datePaid).toLocaleString()}</span>
                                </div>
                              )}
                              {lineItem.amountPaid && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Amount Paid:</label>
                                  <span className="ml-2 text-sm text-gray-900 font-semibold">{formatCurrency(lineItem.amountPaid)}</span>
                                </div>
                              )}
                              {lineItem.paymentMethod && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Payment Method:</label>
                                  <span className="ml-2 text-sm text-gray-900">{lineItem.paymentMethod}</span>
                                </div>
                              )}
                              {lineItem.paymentReference && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Payment Reference:</label>
                                  <span className="ml-2 text-sm text-gray-900 font-mono">{lineItem.paymentReference}</span>
                                </div>
                              )}
                              {lineItem.batchPaymentId && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Batch Payment:</label>
                                  <span className="ml-2 text-sm text-gray-900 font-mono">{lineItem.batchPaymentId}</span>
                                </div>
                              )}
                              {lineItem.paymentSyncedAt && (
                                <div>
                                  <label className="text-xs font-medium text-gray-600">Last Synced:</label>
                                  <span className="ml-2 text-sm text-gray-500">{new Date(lineItem.paymentSyncedAt).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {lineItem.notes && (
                          <div className="mt-3">
                            <label className="text-sm font-medium text-gray-500">Notes</label>
                            <p className="text-gray-900 text-sm">{lineItem.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {workOrder.location && (
              <div>
                <label className="text-sm font-medium text-gray-500">Location</label>
                <p className="text-gray-900">{workOrder.location}</p>
              </div>
            )}

            {workOrder.description && (
              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-gray-900 whitespace-pre-wrap">{workOrder.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {workOrder.serviceType && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Service Type</label>
                  <p className="text-gray-900">{workOrder.serviceType}</p>
                </div>
              )}
              {workOrder.department && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Department</label>
                  <p className="text-gray-900">{workOrder.department}</p>
                </div>
              )}
              {workOrder.team && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Team</label>
                  <p className="text-gray-900">{workOrder.team}</p>
                </div>
              )}
            </div>

            {workOrder.dueDate && (
              <div>
                <label className="text-sm font-medium text-gray-500">Due Date</label>
                <p className="text-gray-900">{new Date(workOrder.dueDate).toLocaleDateString()}</p>
              </div>
            )}

            {workOrder.notes && (
              <div>
                <label className="text-sm font-medium text-gray-500">Notes</label>
                <p className="text-gray-900 whitespace-pre-wrap">{workOrder.notes}</p>
              </div>
            )}

            {workOrder.tags && (
              <div>
                <label className="text-sm font-medium text-gray-500">Tags</label>
                <p className="text-gray-900">{workOrder.tags}</p>
              </div>
            )}

            <div className="pt-4 border-t">
              <p className="text-sm text-gray-500">
                Last updated: {new Date(workOrder.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderManagement;
